// src/app/NotificationsButton.tsx
import { useQuery } from "@tanstack/react-query";

import { useEffect, useRef, useState } from "react";

import { api, call } from "@/api/client";
import { Tag } from "@/design-system";
import { fromNow } from "@/lib/format";
import { NOTIFICATION_TYPE } from "@/lib/labels-extra";

const SEEN_KEY = "adman.notifications.seen";
const readSeen = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? "[]"));
  } catch {
    return new Set();
  }
};

/** الباك إند لا يخزّن حالة "مقروء"، فنتتبعها محلياً: غير المقروء = معرّفات لم تُعرض في القائمة بعد. */
export function NotificationsButton() {
  const { data = [] } = useQuery({ queryKey: ["notifications"], queryFn: () => call(api.GET("/api/notifications")) });
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  const ref = useRef<HTMLDivElement>(null);
  const unread = data.filter((n) => !seen.has(n.id)).length;

  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && data.length) {
      const all = new Set([...seen, ...data.map((n) => n.id)]);
      localStorage.setItem(SEEN_KEY, JSON.stringify([...all].slice(-500)));
      setTimeout(() => setSeen(all), 1500); // نُبقي الشارة لحظة ليرى المستخدم ما هو جديد
    }
  }

  return (
    <div ref={ref} className="relative">
      <button type="button" className="btn relative" onClick={toggle} aria-label={`التنبيهات، ${unread} غير مقروءة`} aria-expanded={open}>
        تنبيهات
        {unread > 0 && (
          <span className="mono absolute -top-[7px] -start-[7px] inline-flex h-[19px] min-w-[19px] items-center justify-center rounded-full text-[10px] text-white"
            style={{ background: "linear-gradient(145deg,#F0736A,#B3352C)", boxShadow: "0 0 14px rgba(240,115,106,.7)" }}>
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="glass absolute end-0 top-[calc(100%+8px)] z-20 w-[320px] overflow-hidden">
          <div className="border-b border-line px-4 py-2.5 text-[13px] font-semibold text-gold-xl">آخر التنبيهات</div>
          <ul className="m-0 max-h-[360px] list-none overflow-auto p-0">
            {data.length === 0 && <li className="p-4 text-center text-[12.5px] text-muted-2">لا توجد تنبيهات</li>}
            {data.slice(0, 20).map((n) => (
              <li key={n.id} className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5 text-[12.5px]">
                <span className="flex flex-col"><span>{NOTIFICATION_TYPE[n.notification_type]}</span><span className="text-[11px] text-muted-3">{fromNow(n.created_at)}</span></span>
                <Tag tone={n.status === "FAILED" ? "bad" : n.status === "PENDING" ? "warn" : "ok"}>{n.channel}</Tag>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
